import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';
import { useLocale } from '../../hooks/useLocale';
import { useOnboarding } from './OnboardingProvider';
import { CompanyFormFields } from '../company/CompanyFormFields.web';
import { useSaveOnboardingCompany } from '../../hooks/useOnboardingApi';
import { useToast } from '../../hooks/useToast';
import { fromWebFile, uploadImageAsset } from '../../lib/image-upload';
import {
  ensureCompanyWebsite,
  getCompanyFormSectionStatus,
  getCompanyFormStatus,
  toCompanySlug,
} from '../../lib/company-form';

const COMPANY_SETUP_STEPS = ['identity', 'details', 'story'] as const;

export function StepCompanySetup() {
  const { t } = useTranslation('auth');
  const { isRTL } = useLocale();
  const { companyData, setCompanyData, nextStep, prevStep } = useOnboarding();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState(companyData.name);
  const [slug, setSlug] = useState(companyData.slug);
  const [industry, setIndustry] = useState(companyData.industry);
  const [employeeCount, setEmployeeCount] = useState(companyData.employeeCount);
  const [location, setLocation] = useState(companyData.location);
  const [website, setWebsite] = useState(companyData.website);
  const [logoUrl, setLogoUrl] = useState(companyData.logo);
  const [slugTouched, setSlugTouched] = useState(!!companyData.slug);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [setupStepIndex, setSetupStepIndex] = useState(0);

  const saveCompany = useSaveOnboardingCompany();

  useEffect(() => {
    if (!slugTouched && name.trim().length > 0) {
      setSlug(toCompanySlug(name));
    }
  }, [name, slugTouched]);

  const formValues = useMemo(
    () => ({
      name,
      slug,
      industry,
      employeeCount,
      location,
      website,
    }),
    [employeeCount, industry, location, name, slug, website],
  );
  const formStatus = useMemo(
    () => getCompanyFormStatus(formValues),
    [formValues],
  );
  const sectionStatus = useMemo(
    () => getCompanyFormSectionStatus(formValues),
    [formValues],
  );
  const setupStep = COMPANY_SETUP_STEPS[setupStepIndex];
  const isLastSetupStep = setupStepIndex === COMPANY_SETUP_STEPS.length - 1;
  const canContinue =
    sectionStatus[setupStep] && !saveCompany.isPending && !isUploadingLogo;

  const stepCopy = [
    {
      title: t('onboarding.companyInfo.steps.identityTitle', {
        defaultValue: 'Create your company page',
      }),
      subtitle: t('onboarding.companyInfo.steps.identitySubtitle', {
        defaultValue: 'Start with the name candidates will recognize.',
      }),
    },
    {
      title: t('onboarding.companyInfo.steps.detailsTitle', {
        defaultValue: 'Tell us about your team',
      }),
      subtitle: t('onboarding.companyInfo.steps.detailsSubtitle', {
        defaultValue: 'These details help candidates understand your company.',
      }),
    },
    {
      title: t('onboarding.companyInfo.steps.storyTitle', {
        defaultValue: 'Bring your page to life',
      }),
      subtitle: t('onboarding.companyInfo.steps.storySubtitle', {
        defaultValue: 'Add your story now. You can refine everything later.',
      }),
    },
  ][setupStepIndex];

  const handleContinue = async () => {
    if (!formStatus.isValid) return;

    try {
      const payload = {
        name: name.trim(),
        slug: formStatus.normalizedSlug,
        industry: industry.trim(),
        employeeCount: employeeCount.trim(),
        location: location.trim(),
        website: ensureCompanyWebsite(website),
        logo: logoUrl || undefined,
      };
      const result = await saveCompany.mutateAsync(payload);

      setCompanyData({
        name: result.company.name || payload.name,
        slug: result.company.slug || payload.slug,
        industry: result.company.industry || payload.industry,
        employeeCount: result.company.employeeCount || payload.employeeCount,
        location: result.company.location || payload.location,
        website: result.company.website || payload.website,
        logo: result.company.logo || payload.logo || '',
      });

      nextStep();
    } catch (error) {
      toast.error(
        t('onboarding.companyInfo.saveFailed', {
          defaultValue: 'Could not save company information',
        }),
        error instanceof Error ? error.message : 'Please try again.',
      );
    }
  };

  const handleBack = () => {
    if (setupStepIndex > 0) {
      setSetupStepIndex((current) => current - 1);
      return;
    }
    prevStep();
  };

  const handlePrimaryAction = () => {
    if (!canContinue) return;
    if (!isLastSetupStep) {
      setSetupStepIndex((current) => current + 1);
      return;
    }
    void handleContinue();
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
      }}
    >
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: 'none' }}
        onChange={(event) => {
          const file = event.target.files?.[0] ?? null;
          if (file) {
            void (async () => {
              const asset = fromWebFile(file);
              try {
                setIsUploadingLogo(true);
                const uploadedUrl = await uploadImageAsset(asset, {
                  kind: 'company-logo',
                  fileName: asset.name,
                  mimeType: asset.type,
                });
                setLogoUrl(uploadedUrl);
                setCompanyData({ logo: uploadedUrl });
              } catch (error) {
                toast.error(
                  t('onboarding.companyInfo.saveFailed', {
                    defaultValue: 'Could not save company information',
                  }),
                  error instanceof Error ? error.message : 'Please try again.',
                );
              } finally {
                URL.revokeObjectURL(asset.uri);
                setIsUploadingLogo(false);
              }
            })();
          }
          event.currentTarget.value = '';
        }}
      />

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <div style={{ maxWidth: 560, marginInline: 'auto', width: '100%' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '36px 1fr 36px',
              alignItems: 'center',
              gap: 12,
              marginBottom: 56,
            }}
          >
            <button
              onClick={handleBack}
              aria-label={t('onboarding.back', { defaultValue: 'Back' })}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 4,
              }}
            >
              <ChevronLeft
                size={24}
                color="var(--text-primary)"
                style={isRTL ? { transform: 'scaleX(-1)' } : undefined}
              />
            </button>
            <div
              style={{
                display: 'flex',
                gap: 6,
                width: '100%',
                alignItems: 'center',
              }}
              aria-label={t('onboarding.companyInfo.steps.counter', {
                current: setupStepIndex + 1,
                total: COMPANY_SETUP_STEPS.length,
                defaultValue: '{{current}} of {{total}}',
              })}
            >
              {COMPANY_SETUP_STEPS.map((step, index) => (
                <span
                  key={step}
                  style={{
                    height: 3,
                    flex: 1,
                    borderRadius: 999,
                    backgroundColor:
                      index <= setupStepIndex
                        ? 'var(--tabbio-button-primary-bg)'
                        : 'var(--gem-outline-variant)',
                  }}
                />
              ))}
            </div>
            <span />
          </div>

          <h1
            style={{
              fontSize: 28,
              fontWeight: 600,
              letterSpacing: '-0.01em',
              color: 'var(--text-primary)',
              marginBottom: 6,
            }}
          >
            {stepCopy.title}
          </h1>
          <p
            style={{
              fontSize: 15,
              lineHeight: '22px',
              color: 'var(--text-secondary)',
              marginBottom: 28,
            }}
          >
            {stepCopy.subtitle}
          </p>

          <CompanyFormFields
            values={formValues}
            onChange={(patch) => {
              if (patch.name !== undefined) setName(patch.name);
              if (patch.slug !== undefined) setSlug(patch.slug);
              if (patch.industry !== undefined) setIndustry(patch.industry);
              if (patch.employeeCount !== undefined)
                setEmployeeCount(patch.employeeCount);
              if (patch.location !== undefined) setLocation(patch.location);
              if (patch.website !== undefined) setWebsite(patch.website);
            }}
            onSlugTouched={() => setSlugTouched(true)}
            selectedLogo={logoUrl || ''}
            onPickLogo={() => fileRef.current?.click()}
            section={setupStep}
          />
        </div>
      </div>

      <div
        style={{
          position: 'sticky',
          bottom: 0,
          borderTop: '1px solid var(--gem-outline-variant)',
          backgroundColor: 'var(--bg)',
          padding: 24,
        }}
      >
        <div style={{ maxWidth: 560, marginInline: 'auto', width: '100%' }}>
          <button
            onClick={handlePrimaryAction}
            disabled={!canContinue}
            style={{
              width: '100%',
              height: 54,
              borderRadius: 9999,
              border: canContinue
                ? 'none'
                : '1px solid var(--gem-outline-variant)',
              cursor: canContinue ? 'pointer' : 'default',
              backgroundColor: canContinue
                ? 'var(--tabbio-button-primary-bg)'
                : 'var(--alpha-05)',
              color: canContinue
                ? 'var(--tabbio-button-primary-text)'
                : 'var(--text-tertiary)',
              fontSize: 16,
              fontWeight: 600,
              fontFamily: 'inherit',
            }}
          >
            {saveCompany.isPending
              ? t('onboarding.companyInfo.saving', {
                  defaultValue: 'Saving...',
                })
              : isLastSetupStep
                ? t('onboarding.companyInfo.createPage', {
                    defaultValue: 'Create company page',
                  })
                : t('onboarding.next', { defaultValue: 'Next' })}
          </button>
        </div>
      </div>
    </div>
  );
}
